import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
registerLocale('es', es);
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  useCreateCrewSettlements,
  useGetSettlementCrops,
  useGetSettlementSuppliers,
} from '@/modules/Settlements/apiHooks';
import { ISettlements } from '@/modules/Settlements/types';
import {
  Button,
  Checkbox,
  NextImage,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import CreateSettlementErrorModal from '@/shared/components/molecules/CreateSettlement/CreateSettlementErrorModal';
import { validationCreateSettlementsSchema } from '@/shared/components/molecules/CreateSettlement/logic';
import { getDateFormat } from '@/shared/components/molecules/CustomDatePicker/logic';
import Translate from '@/shared/components/molecules/Translate';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { getErrorCode } from '@/shared/lib/axios';

interface Props {
  startDate: Date | null;
  endDate: Date | null;
  // dispatchHandler: (obj: Actions) => void;
}
const CreateCrewSettlementModal: React.FC<Props> = ({
  startDate,
  endDate,
  // dispatchHandler,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const { push } = useRouter();
  const queryClient = useQueryClient();
  const { closeModal, openModal } = useModal();
  const [dateRange, setDateRange] = useState({
    startDate,
    endDate,
  });
  const methods = useForm<ISettlements.ICreateSettlementSchema>({
    resolver: yupResolver(validationCreateSettlementsSchema(t)),
    mode: 'all',
    criteriaMode: 'firstError',
    shouldFocusError: true,
  });

  const { mutate, isLoading } = useCreateCrewSettlements();
  const [selectedAsset] = useStore((store) => store?.asset);
  const today = new Date();

  const { data: suppliers } = useGetSettlementSuppliers({
    endDate: format(dateRange.endDate || 0, 'yyyy-MM-dd'),
    startDate: format(dateRange.startDate || 0, 'yyyy-MM-dd'),
    entityId: selectedAsset?.id,
  });
  const supplierIdTracker = methods.watch('supplierId');

  const {
    data: crops,
    mutate: settlementsCropsMutate,
    isSuccess: isSuccessGetCrops,
    isLoading: isLoadingGetCrops,
  } = useGetSettlementCrops({
    endDate: format(dateRange.endDate || 0, 'yyyy-MM-dd'),
    startDate: format(dateRange.startDate || 0, 'yyyy-MM-dd'),
    entityId: selectedAsset?.id,
    supplierId: methods.getValues('supplierId'),
  });
  useEffect(() => {
    if (isSuccessGetCrops)
      methods.setValue(
        'cropsIds',
        crops.map((item) => item.cropId)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessGetCrops]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCreateSettlementError = (error: any) => {
    const code = getErrorCode(error)?.replace('CREATE_SETTLEMENT:', '') || '';
    if (
      code === '#0001' ||
      code === '#0002' ||
      code === '#0003' ||
      code === '#0004' ||
      code === '#0008'
    )
      openModal(
        <CreateSettlementErrorModal
          msg={error.msg}
          code={code}
          settlementId={error.errorResponseData?.settlementId}
        />
      );
    else {
      toast.error(
        <Translate namespace='codes' id={getErrorCode(error) || ''} />,
        {
          id: 'errorID',
        }
      );
    }
  };

  const submitHandler = (
    value: Partial<ISettlements.ICreateSettlementSchema>
  ) => {
    if (dateRange.startDate && dateRange.endDate) {
      mutate(
        {
          cropsIds: value.cropsIds || [],
          endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
          entityId: selectedAsset?.id,
          supplierId: value?.supplierId || 888888888888888,
        },
        {
          onSuccess: (res) => {
            queryClient.invalidateQueries(['settlements']);
            closeModal();
            if (res.data.settlementId) {
              push(`/accounts/settlements/${res.data.settlementId}`);
            }
          },

          onError: (error) => {
            handleCreateSettlementError(error);
          },
        }
      );
    }
  };

  useEffect(() => {
    if (methods.getValues('supplierId')) {
      methods.setValue('cropsIds', []);
      settlementsCropsMutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, supplierIdTracker]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submitHandler)}
        className='flex h-full flex-col justify-between'
      >
        <div className='mx-auto flex w-full flex-col gap-16 tablet:w-2/3 desktop:w-1/3'>
          <div className='mb-24 mt-11'>
            <Typography
              variant='h2'
              weight='semiBold'
              className='pb-0 capitalize'
            >
              {t('new-settlement')}
            </Typography>

            <Typography
              variant='body1'
              weight='regular'
              className='text-surface-10'
            >
              {t('create-crew-settlement-description')}
            </Typography>
          </div>
          <div className='flex flex-col gap-2'>
            {/* label= */}
            <label className='mb-4'>
              <Typography variant='body1' weight='regular'>
                {t('accounting-period')}
              </Typography>
            </label>
            <div className='flex items-center justify-between gap-16'>
              <div className='flex w-full items-center'>
                {dateRange?.startDate && (
                  <DatePicker
                    className='!w-full'
                    wrapperClassName='relative'
                    locale={language}
                    selected={dateRange.startDate}
                    maxDate={dateRange.endDate}
                    onChange={(date) => {
                      if (date) {
                        setDateRange({ ...dateRange, startDate: date });
                        // dispatchHandler({
                        //   type: ActionsTypes.setStartDate,
                        //   payload: date,
                        // });
                      }
                    }}
                    customInput={
                      <Typography
                        variant='body1'
                        weight='bold'
                        className='w-fit cursor-pointer rounded-lg border-xs border-background-disable bg-white px-11 py-9 text-center'
                      >
                        {getDateFormat(
                          dateRange.startDate,
                          language,
                          'dd/MM/YYY'
                        )}
                      </Typography>
                    }
                  />
                )}
                <span className='text-16 mx-16'>{t('to')}</span>
                {dateRange.endDate && (
                  <DatePicker
                    className='!w-full'
                    wrapperClassName='relative'
                    locale={language}
                    selected={dateRange.endDate}
                    maxDate={today}
                    minDate={dateRange.startDate}
                    onChange={(date) => {
                      if (date) {
                        setDateRange({
                          startDate: dateRange.startDate,
                          endDate: date,
                        });
                        // dispatchHandler({
                        //   type: ActionsTypes.setEndDate,
                        //   payload: date,
                        // });
                      }
                    }}
                    customInput={
                      <Typography
                        variant='body1'
                        weight='bold'
                        className='w-fit cursor-pointer rounded-lg border-xs border-background-disable bg-white px-11 py-9 text-center'
                      >
                        {getDateFormat(
                          dateRange.endDate,
                          language,
                          'dd/MM/YYY'
                        )}
                      </Typography>
                    }
                  />
                )}
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-16'>
            <SelectField
              id='supplierId'
              isSearchable
              label={t('team_leader')}
              getLabel={(item: ISettlements.ISupplierItem) =>
                item?.supplierName
              }
              getValue={(item: ISettlements.ISupplierItem) => item?.id}
              required
              list={suppliers || []}
              width='w-full'
              py='0.375rem'
              placeholder={t('team_leader')}
              formatOptionLabel={(option: ISettlements.ISupplierItem) => (
                <div className='flex flex-col'>
                  {option.status === 'INACTIVE' ? (
                    <Typography variant='caption' className='text-surface-10'>
                      {t('inactive')}
                    </Typography>
                  ) : (
                    ''
                  )}
                  <Typography
                    variant='body1'
                    className='text-text-default'
                    weight='semiBold'
                  >
                    {option.supplierName}
                  </Typography>
                </div>
              )}
            />
            <SelectField<ISettlements.ICropItem, true>
              id='cropsIds'
              isSearchable
              isLoading={isLoadingGetCrops}
              label={t('crop-upon-agreement')}
              getLabel={(item: ISettlements.ICropItem) => item?.cropName}
              getValue={(item: ISettlements.ICropItem) => item?.cropId}
              isMulti
              // hideSelectedOptions={false}
              customOption={(option) => (
                <span className='flex items-center gap-9'>
                  <Checkbox
                    className='mx-2  h-16 w-16'
                    checked={option.isSelected}
                    onChange={() => null}
                  />
                  <NextImage
                    useSkeleton
                    src={option?.data?.image}
                    alt={option.label}
                    fill
                    className='h-11 w-11 overflow-hidden rounded-full bg-background-100'
                  />
                  <Typography variant='body2' className='text-primary-default'>
                    {option.label}
                  </Typography>
                </span>
              )}
              // getOptionLabel={(item: {
              //   cropName: string;
              //   cropId: number;
              //   image: string;
              // }) => (
              //   <span className='flex items-center gap-9'>
              //     <NextImage
              //       src={item.image}
              //       alt='image'
              //       fill
              //       width={44}
              //       className='h-11 w-11 overflow-hidden rounded-full bg-background-100'
              //     />
              //     <span>{item.cropName}</span>
              //   </span>
              // )}
              required
              list={crops || []}
              width='w-full'
              py='0.375rem'
              placeholder={t('crop-upon-agreement')}
            />
          </div>
        </div>
        <div className='flex w-full justify-end border-t-xs border-t-neutralVariant-90 pt-24'>
          <Button
            disabled={Boolean(Object.keys(methods.formState.errors).length)}
            size='none'
            className='!rounded-xl py-2 px-11 '
            type='submit'
            loading={isLoading}
          >
            {t('create-settlement')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
export default CreateCrewSettlementModal;
